// @flow
import { useState } from 'react';
import { useAppFlags } from '../utils/featureFlags';
import { toastr } from 'react-redux-toastr';

type MessageDescriptor = { id: string, defaultMessage: string };

type UseSendMessageMessages = {
  errorHeader: MessageDescriptor,
  userNoLongerOptedInErrorMsg: MessageDescriptor,
  failedToSendMessageError: MessageDescriptor,
};

type UseSendMessageParams = {
  refreshCommunicationPreferences: () => Promise<any>,
  onSend: (messageText: string) => Promise<void>,
  intl: any,
  messages: UseSendMessageMessages,
};

/**
 * Validates whether the recipient is still opted in by re-fetching their
 * communication preferences. Returns `true` when the send can proceed.
 *
 * Separated from `handleSendMessage` so that an opt-out failure is handled
 * explicitly rather than via a throw/catch round-trip in the same try block.
 */
async function validateOptIn(
  refreshCommunicationPreferences: () => Promise<any>,
): Promise<boolean> {
  const { data: latestPreferences } = await refreshCommunicationPreferences();
  const latestInfoPreference = latestPreferences?.find(
    (preference) => preference.communicationChannel.type === 'info',
  );
  return latestInfoPreference?.isOptedIn || false;
}

// The code two-way-communication puts on its 422 when the recipient can't be texted. The send
// must opt into `fullError` for the code to survive the API client.
const CONSENT_BLOCK_CODE = 'BLOCKED_OPT_OUT';

function isConsentBlock(error: mixed): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    error.code === CONSENT_BLOCK_CODE
  );
}

/**
 * Shared hook that encapsulates the cross-cutting concerns of sending a message:
 * - isSending state management
 * - smsDoubleOptIn re-validation before send
 * - Standardized error handling (opt-out / generic failure toasts)
 *
 * Callers provide an `onSend` callback that contains the feature-specific
 * send logic (building the payload, calling the API, post-send side-effects).
 */
export function useSendMessage({
  refreshCommunicationPreferences,
  onSend,
  intl,
  messages,
}: UseSendMessageParams): {
  isSending: boolean,
  handleSendMessage: (messageText: string) => Promise<void>,
} {
  const [isSending, setIsSending] = useState(false);
  const { smsDoubleOptIn } = useAppFlags();

  async function handleSendMessage(messageText: string) {
    setIsSending(true);

    try {
      if (smsDoubleOptIn) {
        const isStillOptedIn = await validateOptIn(
          refreshCommunicationPreferences,
        );

        if (!isStillOptedIn) {
          toastr.error(
            intl.formatMessage(messages.errorHeader),
            intl.formatMessage(messages.userNoLongerOptedInErrorMsg),
          );
          return;
        }
      }

      await onSend(messageText);
    } catch (error) {
      toastr.error(
        intl.formatMessage(messages.errorHeader),
        intl.formatMessage(
          isConsentBlock(error)
            ? messages.userNoLongerOptedInErrorMsg
            : messages.failedToSendMessageError,
        ),
      );
    } finally {
      setIsSending(false);
    }
  }

  return { isSending, handleSendMessage };
}
